import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, ArrowRight, Star, Utensils, Truck, Store, Sparkles, Quote, Instagram, Phone, MapPin, Clock, Navigation } from "lucide-react";
import { useData } from "@/context/DataContext";
import { useCart } from "@/context/CartContext";
import PromoBanner from "@/components/PromoBanner";
import { RepTheKing } from "@/components/RepTheKing";
import ItemDialog from "@/components/ItemDialog";
import { BusinessStatusBadge } from "@/components/BusinessStatusBadge";
import { useBusinessStatus } from "@/context/BusinessStatusContext";
import { money, track, api } from "@/lib/api";
import { toast } from "sonner";

const SIGNATURES = [
  { key: "Jerk Chicken", tag: "Scotch Bonnet · Allspice" },
  { key: "Oxtails", tag: "Slow-Braised · Butter Beans" },
  { key: "Curry Chicken", tag: "Golden Curry · Thyme" },
  { key: "Jerk Chicken Rasta Pasta", tag: "Creamy · Tri-Pepper" },
  { key: "Famous Sir. King Mac & Cheese", tag: "Four-Cheese · Baked" },
  { key: "Curry Goat", tag: "Island Classic" },
  { key: "Ultimate Jerk & Surf", tag: "Jerk · Lobster · Shrimp" },
];

function ReviewForm() {
  const [f, setF] = useState({ name: "", text: "", rating: 5 });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async () => {
    if (!f.name.trim() || !f.text.trim()) return toast.error("Please add your name and a review.");
    setBusy(true);
    try {
      await api.post("/reviews", { name: f.name, text: f.text, rating: Number(f.rating) });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your review.");
    } finally { setBusy(false); }
  };
  if (done) return (
    <div data-testid="review-thankyou" className="mt-10 max-w-xl mx-auto text-center rounded-3xl border border-emerald-800/60 bg-[#181D1A] p-8">
      <Star className="w-8 h-8 text-amber-400 fill-amber-400 mx-auto" />
      <p className="mt-3 font-display font-bold text-lg">Thank you!</p>
      <p className="text-[#FDFBF7]/70 text-sm mt-1">Your review was submitted and will appear once our team approves it.</p>
    </div>
  );
  return (
    <div id="leave-review" className="mt-10 max-w-xl mx-auto rounded-3xl border border-emerald-900/60 bg-[#181D1A] p-6">
      <div className="text-center mb-4">
        <span className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400">Share Your Experience</span>
        <h3 className="font-display font-black uppercase text-2xl mt-1">Leave a Review</h3>
      </div>
      <div className="space-y-3">
        <input data-testid="review-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" className="w-full bg-[#101311] rounded-lg px-3 py-3 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
        <div className="flex items-center gap-1">
          <span className="text-sm text-[#FDFBF7]/60 mr-1">Rating:</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} data-testid={`review-star-${n}`} onClick={() => setF({ ...f, rating: n })} className="p-1"><Star className={`w-6 h-6 ${n <= f.rating ? "text-amber-400 fill-amber-400" : "text-emerald-800"}`} /></button>
          ))}
        </div>
        <textarea data-testid="review-text" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} rows={4} placeholder="Tell us about your meal…" className="w-full bg-[#101311] rounded-lg px-3 py-3 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
        <button data-testid="review-submit" onClick={submit} disabled={busy} className="w-full py-3.5 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-[#0A2818] font-display font-black uppercase tracking-wide min-h-[44px]">{busy ? "Submitting…" : "Submit Review"}</button>
      </div>
    </div>
  );
}


export default function Home() {
  const { settings, menu, reviews } = useData();
  const { setOpen } = useCart();
  const navigate = useNavigate();
  const [dialogItem, setDialogItem] = useState(null);
  const { status: bizStatus } = useBusinessStatus();
  const orderLabel = bizStatus?.status === "closed" ? "Pre-Order" : "Order Now";

  const findItem = (name) => menu.items.find((i) => i.name === name);
  const hero = settings?.hero_image;

  const sunrise = findItem("Sir. King Sunrise");
  const drinks = menu.items.filter((i) => i.category_id === "drinks");

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        {hero && <img src={hero} alt="Sir. King Kitchen jerk chicken platter — authentic Jamaican food in Maine" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2818] via-[#0A2818]/75 to-[#0A2818]/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2818] via-transparent to-[#0A2818]/40" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full py-24">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-amber-400 bg-emerald-950/60 border border-amber-500/30 rounded-full px-4 py-2 backdrop-blur-md">
              <Flame className="w-3.5 h-3.5" /> {settings?.booking_note || "Now booking events."}
            </span>
            <h1 className="font-display font-black uppercase tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl mt-6">
              <span className="text-[#FDFBF7]">{settings?.hero_headline_1 || "AUTHENTIC JAMAICAN FLAVOR."}</span><br />
              <span className="text-gradient-gold">{settings?.hero_headline_2 || "MAINE MADE."}</span>
            </h1>
            <p className="text-lg sm:text-xl text-[#FDFBF7]/85 mt-6 max-w-xl leading-relaxed">
              {settings?.hero_subheadline || "Bold Caribbean flavors, made with love and served right here in Maine."}
            </p>
            <p className="text-sm uppercase tracking-[0.2em] text-amber-300/90 font-bold mt-3">Third-generation recipes · Caribbean soul · Maine hospitality</p>
            <div className="mt-5"><BusinessStatusBadge variant="banner" /></div>
            <div className="flex flex-wrap gap-3 mt-8">
              <button data-testid="hero-order-now-cta" onClick={() => { track("order_now_click", { location: "hero" }); navigate("/menu"); }} className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-amber-400 hover:bg-amber-300 text-[#0A2818] font-display font-black uppercase tracking-wide shadow-lg shadow-amber-500/30 active:scale-95 transition-transform">
                {orderLabel} <ArrowRight className="w-5 h-5" />
              </button>
              <button data-testid="hero-book-catering-cta" onClick={() => { track("catering_click", { location: "hero" }); navigate("/catering"); }} className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-emerald-950/70 hover:bg-emerald-900 text-amber-300 border border-emerald-700/60 font-display font-black uppercase tracking-wide backdrop-blur-md active:scale-95 transition-transform">
                Cater Your Event
              </button>
              <button data-testid="hero-view-menu-cta" onClick={() => navigate("/menu")} className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-[#FDFBF7] font-display font-bold uppercase tracking-wide underline decoration-amber-400/60 underline-offset-8 hover:text-amber-300 transition-colors">
                View Menu
              </button>
            </div>
          </div>
        </div>
      </section>

      <PromoBanner />

      {/* WE'RE HIRING */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-6">
        <button data-testid="home-hiring-cta" onClick={() => navigate("/careers")} className="w-full flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-emerald-800/50 to-amber-900/30 border border-amber-500/40 px-6 py-4 text-left hover:border-amber-400 transition-colors">
          <span className="flex items-center gap-3">
            <span className="grid place-items-center w-10 h-10 rounded-full bg-amber-400 text-[#0A2818]"><i className="fa-solid fa-briefcase" /></span>
            <span>
              <span className="block font-display font-black uppercase tracking-wide text-lg">We're Hiring — Join Our Team</span>
              <span className="block text-sm text-[#FDFBF7]/70">Good Food. Great Service. Great People.</span>
            </span>
          </span>
          <span className="px-5 py-2.5 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase text-sm whitespace-nowrap">View Careers</span>
        </button>
      </section>

      {/* SHOP MERCH */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-4">
        <button data-testid="home-merch-cta" onClick={() => navigate("/merch")} className="w-full flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-amber-900/40 to-emerald-900/40 border border-amber-500/40 px-6 py-4 text-left hover:border-amber-400 transition-colors">
          <span className="flex items-center gap-3">
            <span className="grid place-items-center w-10 h-10 rounded-full bg-amber-400 text-[#0A2818]"><i className="fa-solid fa-shirt" /></span>
            <span>
              <span className="block font-display font-black uppercase tracking-wide text-lg">Shop Sir. King Kitchen Merch 👑</span>
              <span className="block text-sm text-[#FDFBF7]/70">Wear the flavor. Represent the King.</span>
            </span>
          </span>
          <span className="px-5 py-2.5 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase text-sm whitespace-nowrap">Shop Merch</span>
        </button>
      </section>

      <RepTheKing />

      {/* QUICK ACTIONS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 -mt-8 relative z-10 grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: Utensils, label: "Order Now", to: "/menu", tid: "quick-order" },
          { icon: Store, label: "Pickup", to: "/delivery-pickup", tid: "quick-pickup" },
          { icon: Truck, label: "Delivery", to: "/delivery-pickup", tid: "quick-delivery" },
          { icon: Sparkles, label: "Catering", to: "/catering", tid: "quick-catering" },
        ].map((a) => (
          <button key={a.label} data-testid={`quick-action-${a.tid}`} onClick={() => navigate(a.to)} className="group flex flex-col items-center gap-2 py-5 rounded-2xl bg-[#181D1A] border border-emerald-900/60 hover:border-amber-500/60 transition-colors">
            <a.icon className="w-6 h-6 text-amber-400 group-hover:scale-110 transition-transform" />
            <span className="font-display font-bold uppercase tracking-wide text-sm">{a.label}</span>
          </button>
        ))}
      </section>

      {/* SIGNATURE FOOD */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400">Signature Dishes</span>
          <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mt-2">The Flavors That Made Us</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SIGNATURES.map((s, idx) => {
            const item = findItem(s.key);
            if (!item) return null;
            const big = idx === 0;
            return (
              <div key={s.key} data-testid={`signature-card-${idx}`} className={`group relative rounded-3xl overflow-hidden border border-emerald-900/60 ${big ? "md:col-span-2 lg:row-span-2 min-h-[280px] lg:min-h-full" : "min-h-[240px]"}`}>
                <img src={item.image} alt={`${s.key} — Sir. King Kitchen`} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A2818] via-black/40 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-6">
                  <span className="text-[11px] uppercase tracking-widest text-amber-400 font-bold">{s.tag}</span>
                  <h3 className={`font-display font-black ${big ? "text-3xl sm:text-4xl" : "text-2xl"}`}>{s.key}</h3>
                  {item.sold_out ? (
                    <span className="mt-3 self-start inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/70 border border-red-400 text-red-400 font-display font-black uppercase text-xs tracking-wide">Sold Out</span>
                  ) : (
                    <button onClick={() => setDialogItem(item)} className="mt-3 self-start inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase text-xs tracking-wide active:scale-95 transition-transform">
                      Add · {money(item.price)}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* STORY */}
      <section className="relative py-20 tropical-grain">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(16,84,54,0.55),transparent_60%),radial-gradient(circle_at_85%_80%,rgba(120,53,15,0.35),transparent_55%)] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={settings?.chef_image} alt="Sir. King Kitchen chef and founder" className="rounded-3xl w-full max-h-[520px] aspect-[4/5] object-cover object-center border border-amber-500/30 shadow-2xl" />
            <div className="absolute -bottom-5 -right-3 sm:right-6 bg-amber-400 text-[#0A2818] rounded-2xl px-5 py-4 font-display font-black uppercase leading-tight shadow-xl">
              3rd Generation<br />Chef
            </div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400">Our Story</span>
            <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mt-2">{settings?.story_title || "The Story Behind Sir. King Kitchen"}</h2>
            <div className="text-[#FDFBF7]/75 mt-5 space-y-4 leading-relaxed whitespace-pre-line">{settings?.story_body}</div>
            <div className="flex flex-wrap gap-2 mt-6">
              {["Family", "Culture", "Flavor", "Community"].map((t) => (
                <span key={t} className="px-4 py-2 rounded-full border border-amber-500/40 text-amber-300 font-display font-bold uppercase text-xs tracking-wider">{t}</span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-7">
              <button onClick={() => navigate("/story")} className="inline-flex items-center gap-2 text-amber-400 font-display font-bold uppercase tracking-wide hover:gap-3 transition-all">Read Our Full Story <ArrowRight className="w-4 h-4" /></button>
              <a data-testid="home-directions-button" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(settings?.address || "135 Congress St, Portland, ME 04101")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-700 hover:bg-emerald-600 text-[#FDFBF7] font-display font-black uppercase tracking-wide text-sm active:scale-95 transition-transform"><Navigation className="w-4 h-4" /> Get Directions</a>
            </div>
          </div>
        </div>
      </section>

      {/* JAMAICA x MAINE */}
      <section className="relative py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400">Two Worlds, One Table</span>
            <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mt-2">From Jamaica to Maine</h2>
            <p className="text-[#FDFBF7]/70 max-w-2xl mx-auto mt-4">Caribbean flavor. Maine hospitality. We blend tropical island soul with the warmth of Maine community — through food, family, and gathering.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="relative rounded-3xl overflow-hidden min-h-[300px] group">
              <img src={settings?.feast_image} alt="Jamaican family-style Caribbean feast" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A2818] to-transparent" />
              <div className="relative h-full flex items-end p-6"><h3 className="font-display font-black text-2xl text-amber-300">Jamaica · Tropical Soul</h3></div>
            </div>
            <div className="relative rounded-3xl overflow-hidden min-h-[300px] group">
              <img src={settings?.maine_image} alt="Maine coastline" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C3D52] to-transparent" />
              <div className="relative h-full flex items-end p-6"><h3 className="font-display font-black text-2xl text-[#FDFBF7]">Maine · Coastal Warmth</h3></div>
            </div>
          </div>
        </div>
      </section>

      {/* DRINKS */}
      {drinks.length > 0 && (
        <section className="py-20 bg-gradient-to-b from-transparent to-[#0A2818]/60">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
            {sunrise && (
              <div className="relative order-2 lg:order-1">
                <img src={sunrise.image} alt="Sir. King Sunrise tropical signature drink" className="rounded-3xl w-full max-w-md mx-auto aspect-square object-cover border border-amber-500/30 gold-glow animate-float" />
              </div>
            )}
            <div className="order-1 lg:order-2">
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400">Sip the Islands</span>
              <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mt-2">Sir. King Sunrise</h2>
              <p className="text-[#FDFBF7]/75 mt-4 leading-relaxed">A colorful tropical signature drink — available in spirited (21+) and virgin versions, alongside fresh juices and rotating seasonal specials.</p>
              <div className="mt-6 space-y-2">
                {drinks.map((d) => (
                  <button key={d.item_id} onClick={() => setDialogItem(d)} className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-[#181D1A] border border-emerald-900/60 hover:border-amber-500/50 transition-colors text-left">
                    <span className="font-display font-bold">{d.name}</span>
                    <span className="text-amber-400 font-bold">{money(d.price)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* REWARDS */}
      {settings?.loyalty?.enabled && (
        <section className="py-10">
          <div className="max-w-5xl mx-auto px-5 sm:px-8">
            <div className="rounded-3xl border border-amber-500/30 bg-gradient-to-r from-amber-950/40 to-emerald-950/40 p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-4 justify-between gold-glow" data-testid="loyalty-banner">
              <div className="flex items-center gap-4">
                <span className="grid place-items-center w-14 h-14 rounded-full bg-amber-400 text-[#0A2818] text-2xl shrink-0"><i className="fa-solid fa-award" /></span>
                <div>
                  <h3 className="font-display font-black uppercase text-2xl">Sir. King Rewards</h3>
                  <p className="text-[#FDFBF7]/75 text-sm mt-1">{settings.loyalty.reward_text}</p>
                </div>
              </div>
              <button onClick={() => navigate("/menu")} className="px-6 py-3 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase tracking-wide whitespace-nowrap active:scale-95 transition-transform">Start Earning</button>
            </div>
          </div>
        </section>
      )}

      {/* REVIEWS */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400">Word of Mouth</span>
            <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mt-2">What Guests Are Saying</h2>
            {reviews.length > 0 && (() => {
              const avg = reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length;
              return (
                <div data-testid="reviews-rating-summary" className="mt-4 inline-flex items-center gap-3 rounded-full border border-amber-500/40 bg-[#181D1A] px-5 py-2.5">
                  <div className="flex items-center gap-0.5 text-amber-400">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-5 h-5 ${n <= Math.round(avg) ? "fill-amber-400" : "fill-transparent"}`} />
                    ))}
                  </div>
                  <span className="font-display font-black text-lg" data-testid="reviews-rating-average">{avg.toFixed(1)}</span>
                  <span className="text-[#FDFBF7]/60 text-sm">· {reviews.length} review{reviews.length > 1 ? "s" : ""}</span>
                </div>
              );
            })()}
          </div>
          {reviews.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-5">
              {reviews.map((r) => (
                <div key={r.review_id || `${r.name}-${r.text}`} className="rounded-3xl bg-[#181D1A] border border-emerald-900/60 p-6">
                  <Quote className="w-8 h-8 text-amber-400/40" />
                  <p className="text-[#FDFBF7]/85 mt-3 leading-relaxed">{r.text}</p>
                  {r.owner_reply && <p data-testid={`review-owner-reply-${r.review_id}`} className="mt-3 pl-3 border-l-2 border-amber-400/60 text-sm text-[#FDFBF7]/70"><span className="font-bold text-amber-300">Sir. King Kitchen: </span>{r.owner_reply}</p>}
                  <div className="flex items-center gap-1 mt-4 text-amber-400">
                    {Array.from({ length: r.rating || 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-amber-400" />)}
                  </div>
                  <div className="font-display font-bold mt-2">{r.name}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center rounded-3xl border border-dashed border-emerald-800/60 py-14 max-w-xl mx-auto">
              <p className="text-[#FDFBF7]/60">Be the first to leave a review below!</p>
            </div>
          )}
          <ReviewForm />
        </div>
      </section>

      {/* SOCIAL */}
      <section className="py-16 bg-[#0A2818]/60 tropical-grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl">Follow The Flavor</h2>
          <p className="text-[#FDFBF7]/70 mt-3">Tag us and see what's cooking.</p>
          <div className="flex justify-center gap-3 mt-6">
            {settings?.social?.instagram && <a href={`https://instagram.com/${settings.social.instagram}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-5 py-3 rounded-full bg-[#181D1A] border border-emerald-800/60 hover:border-amber-500/60 font-semibold"><Instagram className="w-5 h-5 text-amber-400" /> @{settings.social.instagram}</a>}
            {settings?.social?.tiktok && <a href={`https://tiktok.com/@${settings.social.tiktok}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-5 py-3 rounded-full bg-[#181D1A] border border-emerald-800/60 hover:border-amber-500/60 font-semibold"><i className="fa-brands fa-tiktok text-amber-400" /> @{settings.social.tiktok}</a>}
            {settings?.social?.snapchat && <a href={`https://snapchat.com/add/${settings.social.snapchat}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-5 py-3 rounded-full bg-[#181D1A] border border-emerald-800/60 hover:border-amber-500/60 font-semibold"><i className="fa-brands fa-snapchat text-amber-400" /> @{settings.social.snapchat}</a>}
          </div>
        </div>
      </section>

      {/* FINAL CTA — TWO PATHS */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-5">
          <div className="rounded-3xl bg-gradient-to-br from-amber-500/20 to-[#0A2818] border border-amber-500/40 p-9 text-center gold-glow">
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400">I'm Hungry</span>
            <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mt-2">Order Right Now</h2>
            <p className="text-[#FDFBF7]/75 mt-3">Pickup or delivery — fresh Jamaican flavor in minutes.</p>
            <button data-testid="final-order-cta" onClick={() => { track("order_now_click", { location: "final" }); navigate("/menu"); }} className="mt-6 w-full py-4 rounded-full bg-amber-400 hover:bg-amber-300 text-[#0A2818] font-display font-black uppercase tracking-wide active:scale-95 transition-transform">Order Now</button>
          </div>
          <div className="rounded-3xl bg-gradient-to-br from-emerald-800/40 to-[#0A2818] border border-emerald-600/40 p-9 text-center">
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400">I'm Planning an Event</span>
            <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl mt-2">Get a Catering Quote</h2>
            <p className="text-[#FDFBF7]/75 mt-3">Weddings, corporate & celebrations across New England.</p>
            <button data-testid="final-catering-cta" onClick={() => { track("catering_click", { location: "final" }); navigate("/catering"); }} className="mt-6 w-full py-4 rounded-full border border-amber-500/50 text-amber-300 hover:bg-emerald-900/50 font-display font-black uppercase tracking-wide active:scale-95 transition-transform">Get a Catering Quote</button>
          </div>
        </div>
      </section>

      <ItemDialog item={dialogItem} open={!!dialogItem} onClose={() => setDialogItem(null)} />
    </div>
  );
}
